import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, MessageCircle, Send, Bookmark, Volume2, VolumeX, Play, ChevronUp, ChevronDown, Plus, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import FlowCommentSheet from '../components/flow/FlowCommentSheet';
import FlowShareSheet from '../components/flow/FlowShareSheet';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function FlowPage({ user }) {
  const navigate = useNavigate();
  const [flows, setFlows] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [commentFlowId, setCommentFlowId] = useState(null);
  const [shareFlowId, setShareFlowId] = useState(null);
  const containerRef = useRef(null);
  const touchStart = useRef(0);
  const touchDelta = useRef(0);
  const isTransitioning = useRef(false);

  // Hide navigation on mount
  useEffect(() => {
    document.body.classList.add('flow-fullscreen');
    return () => document.body.classList.remove('flow-fullscreen');
  }, []);

  useEffect(() => {
    fetchFlows();
  }, []);

  const fetchFlows = async () => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      const res = await axios.get(`${API}/flow/feed?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFlows(res.data);
    } catch {
      toast.error('Failed to load flows');
    } finally {
      setLoading(false);
    }
  };

  const goTo = useCallback((index) => {
    if (isTransitioning.current || index < 0 || index >= flows.length) return;
    isTransitioning.current = true;
    setCurrentIndex(index);
    const token = localStorage.getItem('dharmbook_token');
    axios.post(`${API}/flow/${flows[index].id}/view`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    }).catch(() => {});
    setTimeout(() => { isTransitioning.current = false; }, 500);
  }, [flows]);

  const handleTouchStart = (e) => { touchStart.current = e.touches[0].clientY; };
  const handleTouchMove = (e) => { touchDelta.current = e.touches[0].clientY - touchStart.current; };
  const handleTouchEnd = () => {
    if (Math.abs(touchDelta.current) > 50) {
      touchDelta.current < 0 ? goTo(currentIndex + 1) : goTo(currentIndex - 1);
    }
    touchDelta.current = 0;
  };

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    if (Math.abs(e.deltaY) > 20) {
      e.deltaY > 0 ? goTo(currentIndex + 1) : goTo(currentIndex - 1);
    }
  }, [currentIndex, goTo]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'ArrowDown') goTo(currentIndex + 1);
    if (e.key === 'ArrowUp') goTo(currentIndex - 1);
  }, [currentIndex, goTo]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    const container = containerRef.current;
    if (container) container.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (container) container.removeEventListener('wheel', handleWheel);
    };
  }, [handleKeyDown, handleWheel]);

  const toggleLike = async (flowId) => {
    const token = localStorage.getItem('dharmbook_token');
    try {
      const res = await axios.post(`${API}/flow/${flowId}/like`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFlows(prev => prev.map(f =>
        f.id === flowId ? { ...f, is_liked: res.data.liked, likes_count: f.likes_count + (res.data.liked ? 1 : -1) } : f
      ));
    } catch { toast.error('Failed'); }
  };

  const toggleSave = async (flowId) => {
    const token = localStorage.getItem('dharmbook_token');
    try {
      const res = await axios.post(`${API}/flow/${flowId}/save`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFlows(prev => prev.map(f =>
        f.id === flowId ? { ...f, is_saved: res.data.saved, saves_count: f.saves_count + (res.data.saved ? 1 : -1) } : f
      ));
    } catch { toast.error('Failed'); }
  };

  const currentFlow = flows[currentIndex];

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black z-[100]">
        <div className="w-10 h-10 border-3 border-[#FF9933] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (flows.length === 0) {
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center bg-black text-white px-6 z-[100]">
        <button onClick={() => navigate('/')} className="absolute top-6 left-4 p-2.5 rounded-full bg-white/10 hover:bg-white/20" data-testid="flow-back-btn">
          <ArrowLeft className="w-5 h-5 text-white" />
        </button>
        <div className="w-24 h-24 rounded-full bg-white/5 flex items-center justify-center mb-6">
          <Play className="w-10 h-10 text-[#FF9933]" />
        </div>
        <h2 className="text-2xl font-semibold mb-3">No Flows Yet</h2>
        <p className="text-gray-400 text-center mb-8 max-w-xs">Be the first to share a short spiritual video</p>
        <button onClick={() => navigate('/flow/upload')} className="px-8 py-3 bg-[#FF9933] hover:bg-[#E68A00] text-white rounded-full font-medium" data-testid="upload-first-flow">
          Upload Flow
        </button>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 z-[100] overflow-hidden select-none"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      tabIndex={0}
      data-testid="flow-page"
    >
      {/* Desktop: Blurred background from current video */}
      <div className="hidden md:block absolute inset-0">
        {currentFlow && (
          <>
            <div 
              className="absolute inset-0 scale-150"
              style={{
                backgroundImage: `url(${currentFlow.thumbnail_url || (currentFlow.video_url?.startsWith('http') ? currentFlow.video_url : `${BACKEND_URL}${currentFlow.video_url}`)})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                filter: 'blur(50px) brightness(0.4) saturate(1.2)',
              }}
            />
            <div className="absolute inset-0 bg-black/40" />
          </>
        )}
      </div>

      {/* Mobile: Pure black background */}
      <div className="md:hidden absolute inset-0 bg-black" />

      {/* Mobile Scroll Hints */}
      {flows.length > 1 && (
        <div className="md:hidden absolute right-3 top-1/2 -translate-y-1/2 z-30 flex flex-col items-center gap-2">
          {/* Scroll Up Indicator */}
          {currentIndex > 0 && (
            <button 
              onClick={() => goTo(currentIndex - 1)}
              className="p-2 rounded-full bg-black/30 backdrop-blur-sm text-white/70 animate-bounce"
              style={{ animationDuration: '2s' }}
            >
              <ChevronUp className="w-5 h-5" />
            </button>
          )}
          
          {/* Position Indicator */}
          <div className="flex flex-col gap-1 py-2">
            {flows.map((_, idx) => (
              <div 
                key={idx}
                className={`w-1 rounded-full transition-all duration-300 ${
                  idx === currentIndex ? 'h-4 bg-[#FF9933]' : 'h-1.5 bg-white/30'
                }`}
              />
            ))}
          </div>
          
          {/* Scroll Down Indicator */}
          {currentIndex < flows.length - 1 && (
            <button 
              onClick={() => goTo(currentIndex + 1)}
              className="p-2 rounded-full bg-black/30 backdrop-blur-sm text-white/70 animate-bounce"
              style={{ animationDuration: '2s' }}
            >
              <ChevronDown className="w-5 h-5" />
            </button>
          )}
        </div>
      )}

      {/* Main Container */}
      <div className="relative h-full w-full flex items-center justify-center">
        {/* Video Container - Full screen on mobile, constrained on desktop */}
        <div className="relative w-full h-full md:h-[calc(100vh-40px)] md:max-h-[95vh] md:w-auto md:aspect-[9/16] md:max-w-[450px] md:rounded-xl md:overflow-hidden">
          {/* Video Stack */}
          <div
            className="absolute inset-0 will-change-transform"
            style={{ 
              transform: `translateY(-${currentIndex * 100}%)`,
              transition: 'transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
            }}
          >
            {flows.map((flow, idx) => (
              <FlowItem
                key={flow.id}
                flow={flow}
                isActive={idx === currentIndex}
                onLike={() => toggleLike(flow.id)}
                onComment={() => setCommentFlowId(flow.id)}
                onShare={() => setShareFlowId(flow.id)}
                onSave={() => toggleSave(flow.id)}
                onProfile={() => navigate(`/profile/${flow.user_id}`)}
                onBack={() => navigate('/')}
                onUpload={() => navigate('/flow/upload')}
              />
            ))}
          </div>
        </div>

        {/* Desktop: Navigation Arrows */}
        <div className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 flex-col gap-3 z-30">
          <button
            onClick={() => goTo(currentIndex - 1)}
            disabled={currentIndex === 0}
            className="w-11 h-11 rounded-full bg-white/10 backdrop-blur-sm hover:bg-white/20 disabled:opacity-30 text-white transition-all flex items-center justify-center"
            data-testid="flow-nav-up"
          >
            <ChevronUp className="w-6 h-6" />
          </button>
          <button
            onClick={() => goTo(currentIndex + 1)}
            disabled={currentIndex === flows.length - 1}
            className="w-11 h-11 rounded-full bg-white/10 backdrop-blur-sm hover:bg-white/20 disabled:opacity-30 text-white transition-all flex items-center justify-center"
            data-testid="flow-nav-down"
          >
            <ChevronDown className="w-6 h-6" />
          </button>
          {/* Flow Counter */}
          <div className="text-white/60 text-xs font-medium text-center mt-2">
            {currentIndex + 1}/{flows.length}
          </div>
        </div>

        {/* Desktop: Back & Upload buttons */}
        <button
          onClick={() => navigate('/')}
          className="hidden md:flex absolute top-4 left-4 w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm hover:bg-white/20 items-center justify-center z-30"
          data-testid="flow-back-desktop"
        >
          <ArrowLeft className="w-5 h-5 text-white" />
        </button>
        <button
          onClick={() => navigate('/flow/upload')}
          className="hidden md:flex absolute top-4 right-4 w-10 h-10 rounded-full bg-[#FF9933] hover:bg-[#E68A00] items-center justify-center z-30 shadow-lg"
          data-testid="flow-upload-desktop"
        >
          <Plus className="w-5 h-5 text-white" strokeWidth={2.5} />
        </button>
        {/* Desktop: Title */}
        <div className="hidden md:block absolute top-4 left-1/2 -translate-x-1/2 z-30">
          <span className="text-white/90 text-base font-semibold">Flow</span>
        </div>
      </div>

      {/* Comment Sheet */}
      {commentFlowId && (
        <FlowCommentSheet
          flowId={commentFlowId}
          onClose={() => setCommentFlowId(null)}
          onCommentAdded={() => {
            setFlows(prev => prev.map(f => f.id === commentFlowId ? { ...f, comments_count: f.comments_count + 1 } : f));
          }}
        />
      )}

      {/* Share Sheet */}
      {shareFlowId && (
        <FlowShareSheet flowId={shareFlowId} onClose={() => setShareFlowId(null)} />
      )}
    </div>
  );
}


function FlowItem({ flow, isActive, onLike, onComment, onShare, onSave, onProfile, onBack, onUpload }) {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true); // Start muted for autoplay to work on mobile
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showFullCaption, setShowFullCaption] = useState(false);
  const [videoError, setVideoError] = useState(false);

  useEffect(() => {
    const vid = videoRef.current;
    if (!vid) return;
    if (isActive) {
      vid.currentTime = 0;
      vid.play().catch((err) => {
        console.error('Video autoplay failed:', err);
        // Try playing muted if autoplay fails
        vid.muted = true;
        setMuted(true);
        vid.play().catch(() => {});
      });
      setPaused(false);
    } else {
      vid.pause();
    }
  }, [isActive]);

  const togglePlay = () => {
    const vid = videoRef.current;
    if (!vid) return;
    vid.paused ? (vid.play(), setPaused(false)) : (vid.pause(), setPaused(true));
  };

  const handleTimeUpdate = () => {
    const vid = videoRef.current;
    if (vid?.duration) setProgress((vid.currentTime / vid.duration) * 100);
  };

  const handleVideoError = (e) => {
    console.error('Video failed to load:', videoSrc, e);
    setVideoError(true);
  };

  // Construct proper video URL
  const getVideoUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    // Prepend backend URL for relative paths
    return `${BACKEND_URL}${url}`;
  };

  const videoSrc = getVideoUrl(flow.video_url);
  
  // Debug logging
  console.log('Flow video URL:', flow.video_url, '-> Full URL:', videoSrc);
  
  const formatCount = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toString() || '0';
  };

  const needsTruncation = flow.caption?.length > 100;

  return (
    <div className="relative w-full h-screen md:h-full bg-black" data-testid={`flow-item-${flow.id}`}>
      {/* Video */}
      {videoError ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900 text-white">
          <Play className="w-16 h-16 text-gray-500 mb-4" />
          <p className="text-gray-400 text-sm">Video failed to load</p>
          <button 
            onClick={() => setVideoError(false)}
            className="mt-4 px-4 py-2 bg-[#FF9933] rounded-full text-sm"
          >
            Retry
          </button>
        </div>
      ) : (
        <video
          ref={videoRef}
          src={videoSrc}
          className="absolute inset-0 w-full h-full object-cover"
          loop
          muted={muted}
          playsInline
          autoPlay={isActive}
          preload="auto"
          onClick={togglePlay}
          onTimeUpdate={handleTimeUpdate}
          onError={handleVideoError}
        />
      )}

      {/* Gradient Overlays */}
      <div className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-b from-black/60 to-transparent z-10 pointer-events-none" />
      <div className="absolute bottom-0 left-0 right-0 h-56 bg-gradient-to-t from-black/80 via-black/40 to-transparent z-10 pointer-events-none" />

      {/* Mobile: Top Bar */}
      <div className="md:hidden absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-4 pt-3 safe-area-top">
        <button onClick={(e) => { e.stopPropagation(); onBack(); }} className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-white/10" data-testid="flow-back-btn">
          <ArrowLeft className="w-6 h-6 text-white drop-shadow-lg" />
        </button>
        <span className="text-white text-sm font-semibold drop-shadow-lg">Flow</span>
        <button onClick={(e) => { e.stopPropagation(); onUpload(); }} className="w-9 h-9 rounded-full bg-[#FF9933] flex items-center justify-center shadow-lg" data-testid="flow-upload-btn">
          <Plus className="w-5 h-5 text-white" strokeWidth={2.5} />
        </button>
      </div>

      {/* Pause Indicator */}
      {paused && (
        <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
          <div className="w-20 h-20 rounded-full bg-black/40 flex items-center justify-center">
            <Play className="w-10 h-10 text-white ml-1" fill="white" />
          </div>
        </div>
      )}

      {/* Progress Bar */}
      <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-white/20 z-30">
        <div className="h-full bg-white transition-all duration-100" style={{ width: `${progress}%` }} />
      </div>

      {/* Right: Action Panel */}
      <div className="absolute right-3 md:right-4 bottom-24 md:bottom-28 z-20 flex flex-col items-center gap-3">
        {/* Profile Avatar */}
        <button onClick={(e) => { e.stopPropagation(); onProfile(); }} className="relative mb-1" data-testid={`flow-profile-avatar-${flow.id}`}>
          <img
            src={flow.user_avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(flow.user_name)}&background=FF9933&color=fff`}
            alt={flow.user_name}
            className="w-11 h-11 rounded-full object-cover ring-2 ring-white"
          />
          <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-5 h-5 bg-[#FF9933] rounded-full flex items-center justify-center ring-2 ring-black">
            <Plus className="w-3 h-3 text-white" strokeWidth={3} />
          </div>
        </button>

        {/* Like */}
        <button onClick={(e) => { e.stopPropagation(); onLike(); }} className="flex flex-col items-center" data-testid={`flow-like-${flow.id}`}>
          <Heart className={`w-6 h-6 transition-transform active:scale-75 ${flow.is_liked ? 'fill-red-500 text-red-500' : 'text-white'}`} style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }} />
          <span className="text-white text-[10px] font-semibold mt-0.5">{formatCount(flow.likes_count)}</span>
        </button>

        {/* Comment */}
        <button onClick={(e) => { e.stopPropagation(); onComment(); }} className="flex flex-col items-center" data-testid={`flow-comment-${flow.id}`}>
          <MessageCircle className="w-6 h-6 text-white active:scale-75" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }} />
          <span className="text-white text-[10px] font-semibold mt-0.5">{formatCount(flow.comments_count)}</span>
        </button>

        {/* Share */}
        <button onClick={(e) => { e.stopPropagation(); onShare(); }} className="flex flex-col items-center" data-testid={`flow-share-${flow.id}`}>
          <Send className="w-5 h-5 text-white active:scale-75" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }} />
          <span className="text-white text-[10px] font-semibold mt-0.5">{formatCount(flow.shares_count || 0)}</span>
        </button>

        {/* Save */}
        <button onClick={(e) => { e.stopPropagation(); onSave(); }} className="flex flex-col items-center" data-testid={`flow-save-${flow.id}`}>
          <Bookmark className={`w-6 h-6 active:scale-75 ${flow.is_saved ? 'fill-white text-white' : 'text-white'}`} style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }} />
        </button>

        {/* Mute */}
        <button onClick={(e) => { e.stopPropagation(); setMuted(!muted); }} className="mt-1" data-testid={`flow-mute-${flow.id}`}>
          {muted ? <VolumeX className="w-5 h-5 text-white/80" /> : <Volume2 className="w-5 h-5 text-white/80" />}
        </button>
      </div>

      {/* Bottom: User Info & Caption */}
      <div className="absolute bottom-5 md:bottom-6 left-4 right-16 z-20 safe-area-bottom">
        <button onClick={(e) => { e.stopPropagation(); onProfile(); }} className="flex items-center gap-2 mb-2" data-testid={`flow-profile-${flow.id}`}>
          <span className="text-white font-bold text-[15px] drop-shadow-lg">@{flow.user_name}</span>
          <span className="text-white/70 text-sm">• Follow</span>
        </button>
        {flow.caption && (
          <div className="max-w-[90%]">
            <p className="text-white text-[13px] leading-[1.4] drop-shadow-md">
              {showFullCaption ? flow.caption : flow.caption?.slice(0, 100)}
              {needsTruncation && !showFullCaption && (
                <button onClick={(e) => { e.stopPropagation(); setShowFullCaption(true); }} className="text-white/60 ml-1">...more</button>
              )}
            </p>
            {showFullCaption && needsTruncation && (
              <button onClick={(e) => { e.stopPropagation(); setShowFullCaption(false); }} className="text-white/60 text-sm mt-1">less</button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
